import { type ApiClient } from '../../../shared/api/api-client.js';

/**
 * Booking API — wraps the gateway calls the guest booking flow needs.
 *
 * Matches the old elevebooking engine flow:
 * 1. createPencilReservation → POST /v1/guest/reservations
 * 2. createPaymentSession (Windcave) → POST /v1/guest/payment/initiate
 * 3. makePayment (Quickstream) → POST /v1/guest/payment/confirm
 * 4. getWPaymentSession (Windcave callback) → GET /v1/guest/payment/session/:propertyId/:sessionId
 * 5. getReservationById → GET /v1/guest/reservations/:id
 */

export interface GuestDetails {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly marketingOptIn?: boolean;
  readonly address?: {
    /** Optional — the checkout form collects no street line. */
    readonly line1?: string;
    readonly line2?: string;
    readonly city?: string;
    readonly suburb?: string;
    readonly state?: string;
    readonly postalCode?: string;
    readonly countryCode?: string;
  };
}

export interface GuestProfileResponse {
  readonly guestId: string;
  readonly created: boolean;
}

export interface ReservationResponse {
  readonly id: string;
  readonly bookingReference: string;
  readonly replayed: boolean;
  readonly guestId: string;
  readonly pmsGuestId: string | null;
  readonly pmsReservationId: string | null;
  /** Short-lived reservation-scoped JWT for the confirmation page. */
  readonly reservationToken: string;
}

export interface CreateReservationInput {
  readonly propertyId: string;
  readonly guest: GuestDetails;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly rooms: number;
  readonly adults: number;
  readonly children: number;
  readonly infants: number;
  readonly specialRequests?: string;
  readonly currency: string;
  readonly totalAmount: string;
  readonly taxes: string;
  readonly fees: string;
  readonly discounts: string;
  readonly grandTotal: string;
  readonly idempotencyKey?: string;
  readonly items: readonly {
    readonly roomTypeId: string;
    readonly ratePlanId: string;
    readonly checkIn: string;
    readonly checkOut: string;
    readonly adults: number;
    readonly children: number;
    readonly infants: number;
    readonly total: string;
    readonly quote: Record<string, unknown>;
    /**
     * The durable server-side quote this item was priced from. When present
     * on every item, the reservation API derives all financial totals from
     * the stored quote rather than trusting the client-supplied money above.
     */
    readonly quoteId?: string | undefined;
  }[];
}

export interface PaymentInitiateResponse {
  readonly paymentId: string;
  readonly redirectUrl: string;
  readonly gatewayReference: string;
  readonly status: string;
}

export interface PaymentConfirmResponse {
  readonly paymentId: string;
  readonly status: string;
  readonly confirmed: boolean;
}

export interface PaymentSessionResponse {
  readonly sessionId: string;
  readonly status: string;
  readonly paymentId: string | null;
}

export interface GuestReservationDetail {
  readonly id: string;
  readonly bookingReference: string;
  readonly status: string;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly nights: number;
  readonly adults: number;
  readonly children: number;
  readonly infants: number;
  readonly rooms: number;
  readonly currency: string;
  /** Booked-on timestamp (reservation creation). */
  readonly createdAt?: string | undefined;
  readonly guest: {
    readonly guestId: string;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly email: string | null;
    readonly phone: string | null;
    readonly countryCode?: string | null | undefined;
    readonly country?: string | null | undefined;
    readonly pmsGuestId: string | null;
  };
  readonly property: {
    readonly propertyId: string;
    readonly name: string;
    readonly displayName: string | null;
    readonly addressLine1: string | null;
    readonly addressLine2: string | null;
    readonly postCode: string | null;
    /** The property's settlement currency, when one is configured. */
    readonly currency?: string | null | undefined;
    readonly defaultCheckInTime?: string | null | undefined;
    readonly defaultCheckOutTime?: string | null | undefined;
    readonly coverImageUrl: string | null;
    readonly coverImageAlt: string | null;
    readonly pmsConfigured: boolean;
    readonly pmsPropertyId: string | null;
  };
  /** Display-only payment summary; card fields are brand + last four only. */
  readonly payment?:
    | {
        readonly status: string;
        readonly gateway: string | null;
        readonly methodType: string | null;
        readonly cardBrand: string | null;
        readonly cardLast4: string | null;
        readonly amountCaptured: string | null;
        readonly currency: string;
        /** Total captured across all of the reservation's payments. */
        readonly amountPaid: string;
        /** Share of the grand total the guest still owes at the property. */
        readonly amountDueOnArrival: string;
      }
    | null
    | undefined;
  readonly items: readonly {
    readonly roomTypeId: string;
    readonly roomTypeName: string;
    readonly ratePlanId: string | null;
    readonly ratePlanName: string;
    readonly imageUrl?: string | null | undefined;
    readonly imageAlt?: string | null | undefined;
    readonly cancellationPolicyName?: string | null | undefined;
    readonly paymentPolicyName?: string | null | undefined;
    readonly checkIn: string;
    readonly checkOut: string;
    readonly nights: number;
    readonly adults: number;
    readonly children: number;
    readonly infants: number;
    readonly total: string;
  }[];
  readonly amounts: {
    readonly totalAmount: string;
    readonly taxes: string;
    readonly fees: string;
    readonly discounts: string;
    readonly grandTotal: string;
    /** Card surcharge the gateway added on top of `grandTotal`; null when none. */
    readonly surcharge: string | null;
  };
  readonly deposit: {
    readonly amount: string;
    readonly dueDate: string | null;
  } | null;
  readonly cancellationDeadline: string | null;
  readonly modificationDeadline: string | null;
  readonly specialRequests: string | null;
  readonly pmsReservationId: string | null;
}

/**
 * Finds or creates a guest profile by email — the old flow's
 * getGuestByEmail + updateGuest (create-if-not-found) combination.
 */
export async function findOrCreateGuest(
  api: ApiClient,
  input: {
    readonly email: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly marketingConsent?: boolean;
  },
): Promise<GuestProfileResponse> {
  return await api.post<GuestProfileResponse>('/v1/guest/profile', input);
}

export interface GuestProfileDetails {
  readonly guestId: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  /** The member's primary address, when a past booking stored one. */
  readonly address?: {
    readonly line1: string | null;
    readonly line2: string | null;
    readonly city: string | null;
    readonly suburb: string | null;
    readonly state: string | null;
    readonly postalCode: string | null;
    readonly countryCode: string | null;
  } | null;
}

/** Shared key so the signup flow can seed the profile it just wrote and the
 * checkout prefill reads it without a second GET. */
export function guestProfileQueryKey(guestId: string): readonly [string, string] {
  return ['guest-profile', guestId];
}

/**
 * Reads the signed-in guest's own profile — the member checkout prefill.
 * The endpoint is owner-scoped, so it is only meaningful while authenticated.
 */
export async function getGuestProfile(
  api: ApiClient,
  guestId: string,
): Promise<GuestProfileDetails> {
  return await api.get<GuestProfileDetails>(`/v1/guest/profile/${guestId}`);
}

/**
 * Updates a guest profile — the old flow's updateGuest.
 */
export async function updateGuestProfile(
  api: ApiClient,
  guestId: string,
  input: {
    readonly firstName?: string;
    readonly lastName?: string;
    readonly email?: string;
    readonly phone?: string;
  },
): Promise<{ readonly guestId: string }> {
  return await api.patch<{ readonly guestId: string }>(`/v1/guest/profile/${guestId}`, input);
}

/**
 * Records marketing consent — the old flow's acceptGuestMarketingConsent.
 */
export async function acceptMarketingConsent(
  api: ApiClient,
  guestId: string,
  marketingConsent: boolean,
): Promise<{ readonly guestId: string }> {
  return await api.post<{ readonly guestId: string }>(
    `/v1/guest/profile/${guestId}/marketing-consent`,
    { marketingConsent },
  );
}

/** Creates a pencil reservation — the old flow's createPencilReservation. */
export async function createGuestReservation(
  api: ApiClient,
  input: CreateReservationInput,
): Promise<ReservationResponse> {
  return await api.post<ReservationResponse>('/v1/guest/reservations', input, input.idempotencyKey);
}

/** Fetches a reservation by ID — the old flow's getReservationById. */
export async function getGuestReservation(
  api: ApiClient,
  reservationId: string,
): Promise<Record<string, unknown>> {
  return await api.get<Record<string, unknown>>(`/v1/guest/reservations/${reservationId}`);
}

/** Fetches the full guest-facing detail for a reservation. */
export async function getGuestReservationDetail(
  api: ApiClient,
  reservationId: string,
): Promise<GuestReservationDetail> {
  return await api.get<GuestReservationDetail>(`/v1/guest/reservations/${reservationId}/detail`);
}

/** Initiates a Windcave hosted payment session — the old flow's createPaymentSession. */
export async function initiateGuestPayment(
  api: ApiClient,
  input: {
    readonly reservationId: string;
    readonly propertyId: string;
    readonly gateway: string;
    readonly amount: string;
    readonly currency: string;
    readonly returnUrl: string;
    readonly cancelUrl: string;
    readonly idempotencyKey?: string;
    readonly merchantReference?: string;
  },
): Promise<PaymentInitiateResponse> {
  return await api.post<PaymentInitiateResponse>('/v1/guest/payment/initiate', input);
}

/** Confirms a payment — the old flow's makePayment. */
export async function confirmGuestPayment(
  api: ApiClient,
  input: {
    readonly paymentId: string;
    readonly singleUseTokenId?: string;
    readonly amount?: string;
    readonly currency?: string;
  },
): Promise<PaymentConfirmResponse> {
  return await api.post<PaymentConfirmResponse>('/v1/guest/payment/confirm', input);
}

/** Looks up a Windcave payment session — the old flow's getWPaymentSession. */
export async function getPaymentSession(
  api: ApiClient,
  propertyId: string,
  sessionId: string,
): Promise<PaymentSessionResponse> {
  return await api.get<PaymentSessionResponse>(
    `/v1/guest/payment/session/${propertyId}/${sessionId}`,
  );
}

export interface PaymentPolicyResponse {
  readonly gateway: string;
  readonly gatewayType: number;
  readonly captureAtBooking: boolean;
  readonly depositPercent: number | null;
  readonly transactionType: 'auth' | 'purchase';
  readonly amount: string;
  readonly currency: string;
  readonly dueAtProperty: boolean;
  readonly publishableKey?: string;
  readonly businessCode?: string;
  readonly scriptUrl?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Reads the payment policy for the selected rate plan and amount. The
 * endpoint validates ratePlanId as a UUID, so sentinel values like 'MIXED'
 * (a mixed-rate composition has no single plan) are dropped — the
 * property-level policy then applies. */
export async function getPaymentPolicy(
  api: ApiClient,
  input: {
    readonly propertyId: string;
    readonly gateway?: string;
    readonly ratePlanId?: string;
    /**
     * The policy the selected quote was sold under — the preview's source of
     * truth. A mixed-rate offer's `ratePlanId` is the sentinel 'MIXED', which
     * the endpoint cannot resolve, so the rate-plan fallback would preview a
     * different policy than the charge applies.
     */
    readonly paymentPolicyId?: string;
    readonly amount: string;
    readonly currency: string;
  },
): Promise<PaymentPolicyResponse> {
  return await api.get<PaymentPolicyResponse>('/v1/guest/payment/policy', {
    ...input,
    ...(input.ratePlanId !== undefined && !UUID_RE.test(input.ratePlanId)
      ? { ratePlanId: undefined }
      : {}),
    ...(input.paymentPolicyId !== undefined && !UUID_RE.test(input.paymentPolicyId)
      ? { paymentPolicyId: undefined }
      : {}),
  });
}

export interface CountryOption {
  /** ISO 3166-1 alpha-2 code — what the guest address write path accepts. */
  readonly value: string;
  readonly label: string;
  readonly meta?: { readonly phonePrefix?: string | null | undefined } | undefined;
}

const COUNTRY_OPTIONS_LIMIT = 250;
const COUNTRY_OPTIONS_CACHE_KEY = 'ibe.countryOptions';
const COUNTRY_OPTIONS_CACHE_TTL_MS = 86_400_000;

interface CachedCountryOptions {
  readonly fetchedAt: number;
  readonly options: readonly CountryOption[];
}

function readCachedCountryOptions(): readonly CountryOption[] | null {
  try {
    const raw = localStorage.getItem(COUNTRY_OPTIONS_CACHE_KEY);
    if (raw === null) return null;
    const cached = JSON.parse(raw) as CachedCountryOptions;
    return typeof cached.fetchedAt === 'number' &&
      Array.isArray(cached.options) &&
      Date.now() - cached.fetchedAt < COUNTRY_OPTIONS_CACHE_TTL_MS
      ? cached.options
      : null;
  } catch {
    // Storage unavailable or a corrupt entry — fall through to the network.
    return null;
  }
}

/**
 * The same country vocabulary the admin property form reads, via the public
 * reference options endpoint. `limit` is the endpoint's default (250), stated
 * so the whole ISO list arrives in one page. The list changes on the order
 * of years, so the response is cached in localStorage for a day — repeat
 * checkouts and page reloads read it without another network call.
 */
export async function getCountryOptions(api: ApiClient): Promise<readonly CountryOption[]> {
  const cached = readCachedCountryOptions();
  if (cached !== null) return cached;
  const options = await api.get<readonly CountryOption[]>('/v1/countries/options', {
    limit: COUNTRY_OPTIONS_LIMIT,
  });
  try {
    localStorage.setItem(
      COUNTRY_OPTIONS_CACHE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), options } satisfies CachedCountryOptions),
    );
  } catch {
    // Storage full or disabled — the fetch still succeeded.
  }
  return options;
}

export interface SuburbOption {
  /** `postcode - name` — the legacy combined value written into the field. */
  readonly value: string;
  readonly label: string;
  readonly meta?: { readonly name: string; readonly state: string; readonly postcode: string };
}

/**
 * Australian suburb suggestions for the postal-code autocomplete, matching
 * the legacy `/auspost/suburbs` fixture served by the reference service.
 */
export async function getSuburbOptions(
  api: ApiClient,
  postcode: string,
): Promise<readonly SuburbOption[]> {
  return await api.get<readonly SuburbOption[]>('/v1/suburbs/options', { postcode });
}
